import axiosInstance from '../config/axios.config';

class EstablishmentService {
    // Get all establishment types
    getEstablishmentTypes() {
        return axiosInstance.get('/user-details/establishment/types');
    }

    // Get requirements for a specific establishment type
    getEstablishmentRequirements(type) {
        return axiosInstance.get(`/user-details/establishment/requirements/${type}`);
    }

    // Save establishment details
    saveEstablishmentDetails(data) {
        return axiosInstance.post('/user-details/establishment/details', data);
    }

    // Get current establishment details
    getEstablishmentDetails() {
        return axiosInstance.get('/user-details/establishment/details');
    }

    // Upload establishment document
    uploadDocument(formData) {
        return axiosInstance.post('/user-details/establishment/document/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    // Delete establishment document
    deleteDocument(documentId) {
        return axiosInstance.delete(`/user-details/establishment/document/${documentId}`);
    }

    // Get validation status
    getValidationStatus() {
        return axiosInstance.get('/user-details/establishment/validation');
    }

    /**
     * Verify a GSTIN and get the mapped autofill payload.
     *
     * Backend behaviour: cache-first — if this user has already verified
     * the same GSTIN it returns the cached row WITHOUT calling Surepass
     * (saves real ₹ on every retry). Only a genuinely new GSTIN triggers
     * a paid lookup.
     *
     * Returns: { success, cached, data: {
     *   gstin, pan_number, business_name, legal_name,
     *   constitution_of_business, type_of_establishment,
     *   gstin_status, is_active, date_of_registration, verified_at
     * } }
     */
    async verifyGstin(gstin) {
        try {
            const response = await axiosInstance.post('/user-details/establishment/gstin-verify', {
                id_number: (gstin || '').toUpperCase().trim(),
            });
            return {
                success: !!response.data?.success,
                cached:  !!response.data?.cached,
                data:    response.data?.data || null,
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message
                    || 'Could not verify GSTIN. Please check the number and retry.',
                errors:  error.response?.data?.errors || null,
                status:  error.response?.status,
            };
        }
    }

    // Field configurations for each establishment type
    getFieldsForType(type) {
        const fieldConfigs = {
            individual: [
                { name: 'individual_name', label: 'Name', required: true, type: 'text' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            proprietorship: [
                { name: 'proprietorship_establishment_name', label: 'Name of the Establishment', required: true, type: 'text' },
                { name: 'proprietorship_name', label: 'Proprietorship Name', required: true, type: 'text' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            partnership: [
                { name: 'partnership_establishment_name', label: 'Name of the Establishment', required: true, type: 'text' },
                { name: 'partnership_partner_name', label: 'Partner Name', required: true, type: 'text' },
                { name: 'partnership_no_of_partners', label: 'No of Partners', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            llp: [
                { name: 'llp_name', label: 'Name of the LLP', required: true, type: 'text' },
                { name: 'llp_partner_name', label: 'Partner Name', required: true, type: 'text' },
                { name: 'llp_no_of_partners', label: 'No. of Partners', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            private_limited: [
                { name: 'pvt_ltd_name', label: 'Name of the Pvt Ltd', required: true, type: 'text' },
                { name: 'pvt_ltd_director_name', label: 'Director Name', required: true, type: 'text' },
                { name: 'pvt_ltd_no_of_directors', label: 'No. of Directors', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            public_limited: [
                { name: 'public_ltd_name', label: 'Name of the Public Ltd', required: true, type: 'text' },
                { name: 'public_ltd_director_name', label: 'Director Name', required: true, type: 'text' },
                { name: 'public_ltd_no_of_directors', label: 'No. of Directors', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            one_man_company: [
                { name: 'omc_name', label: 'Name of the One Man Company', required: true, type: 'text' },
                { name: 'omc_director_name', label: 'Director Name', required: true, type: 'text' },
                { name: 'omc_no_of_directors', label: 'No. of Directors', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            society: [
                { name: 'society_name', label: 'Name of the Society', required: true, type: 'text' },
                { name: 'society_director_name', label: 'Director Name', required: true, type: 'text' },
                { name: 'society_no_of_directors', label: 'No. of Directors', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            trust: [
                { name: 'trust_name', label: 'Name of the Trust', required: true, type: 'text' },
                { name: 'trust_trustee_name', label: 'Director Name', required: true, type: 'text' },
                { name: 'trust_no_of_trustees', label: 'No. of Directors', required: true, type: 'number' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            nbfc: [
                { name: 'nbfc_name', label: 'Name of the NBFC', required: true, type: 'text' },
                { name: 'nbfc_authorized_person_name', label: 'Authorized Person Name', required: true, type: 'text' },
                { name: 'city', label: 'City', required: true, type: 'text' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            bank: [
                { name: 'bank_name', label: 'Name of the Bank', required: true, type: 'text' },
                { name: 'bank_authorized_person_name', label: 'Authorized Person Name', required: true, type: 'text' },
                { name: 'city', label: 'City', required: true, type: 'text' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ],
            mfi: [
                { name: 'mfi_name', label: 'Name of the MFI', required: true, type: 'text' },
                { name: 'mfi_authorized_person_name', label: 'Authorized Person Name', required: true, type: 'text' },
                { name: 'city', label: 'City', required: true, type: 'text' },
                { name: 'designation', label: 'Designation', required: true, type: 'text' }
            ]
        };

        return fieldConfigs[type] || [];
    }
}

export default new EstablishmentService();